import React from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import useRecipeDetail from '../hooks/useRecipeDetail';
import { formatTime } from '../utils/time';
import { getKiLabel, getKiTooltip } from '../utils/recipe';
import { RECIPE_DETAIL as RD } from '../constants/strings';
import {
  IconClose, IconWarning, IconClipboardCopy, IconClipboardDone,
  IconNavShopping, IconWand, IconPhoto,
  IconBrandWhatsapp, IconBrandFacebook, IconBrandX, IconBrandTelegram, IconBrandLinkedin, IconMail,
} from '../components/icons';
import './RecipeDetail.css';

const WARM_CATS  = ['Hauptgerichte','Fleisch & Geflügel','Geflügel','Grill & BBQ','Aus dem Ofen','Aufläufe & Gratins','Festliche Gerichte'];
const COOL_CATS  = ['Suppen & Eintöpfe','Fisch & Meeresfrüchte','Pasta & Nudeln','Reis & Getreide'];
const GREEN_CATS = ['Beilagen','Vegetarisch','Vegan','Salate','Gemüse & Beilagen','Hülsenfrüchte'];
const SWEET_CATS = ['Desserts & Süßes','Frühstück & Brunch','Snacks & Fingerfood','Pizza & Flammkuchen'];

function catColorClass(cat) {
  if (!cat) return 'cat-neutral';
  if (WARM_CATS.includes(cat))  return 'cat-warm';
  if (COOL_CATS.includes(cat))  return 'cat-cool';
  if (GREEN_CATS.includes(cat)) return 'cat-green';
  if (SWEET_CATS.includes(cat)) return 'cat-sweet';
  return 'cat-neutral';
}

const CATEGORY_ICONS = {
  'Hauptgerichte':        '🍽️',
  'Suppen & Eintöpfe':    '🥘',
  'Salate':               '🥗',
  'Pasta & Nudeln':       '🍝',
  'Reis & Getreide':      '🍚',
  'Fleisch & Geflügel':   '🥩',
  'Geflügel':             '🍗',
  'Fisch & Meeresfrüchte':'🐟',
  'Vegetarisch':          '🥦',
  'Vegan':                '🌱',
  'Desserts & Süßes':     '🍰',
  'Frühstück & Brunch':   '🥐',
  'Snacks & Fingerfood':  '🍿',
  'Pizza & Flammkuchen':  '🍕',
  'Aufläufe & Gratins':   '🫕',
  'Grill & BBQ':          '🔥',
  'Aus dem Ofen':         '♨️',
  'Hülsenfrüchte':        '🫘',
  'Gemüse & Beilagen':    '🥕',
  'Festliche Gerichte':   '🎉',
};

const ALLERGEN_LABELS = {
  gluten:     { label: 'Gluten',         icon: '🌾', desc: 'Glutenhaltiges Getreide und daraus hergestellte Erzeugnisse.',       foods: 'Weizen, Roggen, Gerste, Hafer, Dinkel, Brot, Pasta' },
  krebstiere: { label: 'Krebstiere',     icon: '🦐', desc: 'Krebstiere und daraus hergestellte Erzeugnisse.',                   foods: 'Garnelen, Hummer, Krabben, Langusten' },
  eier:       { label: 'Eier',           icon: '🥚', desc: 'Eier von Geflügel und daraus hergestellte Erzeugnisse.',            foods: 'Hühnerei, Mayonnaise, Nudeln, Backwaren' },
  fisch:      { label: 'Fisch',          icon: '🐟', desc: 'Fisch und daraus hergestellte Erzeugnisse.',                        foods: 'Lachs, Thunfisch, Kabeljau, Fischsauce, Worcestershire' },
  erdnuesse:  { label: 'Erdnüsse',       icon: '🥜', desc: 'Erdnüsse und daraus hergestellte Erzeugnisse.',                    foods: 'Erdnussbutter, Erdnussöl, Satay-Sauce, Mandelgebäck' },
  soja:       { label: 'Soja',           icon: '🫘', desc: 'Sojabohnen und daraus hergestellte Erzeugnisse.',                   foods: 'Tofu, Sojasoße, Tempeh, Sojaöl, Miso' },
  milch:      { label: 'Milch',          icon: '🥛', desc: 'Milch und Milcherzeugnisse einschließlich Laktose.',                foods: 'Käse, Butter, Joghurt, Sahne, Schokolade' },
  nuesse:     { label: 'Schalenfrüchte', icon: '🌰', desc: 'Schalenfrüchte und daraus hergestellte Erzeugnisse.',              foods: 'Mandeln, Haselnüsse, Walnüsse, Cashews, Pistazien' },
  sellerie:   { label: 'Sellerie',       icon: '🥬', desc: 'Sellerie und daraus hergestellte Erzeugnisse.',                    foods: 'Staudensellerie, Selleriesalz, Suppen, Gewürzmischungen' },
  senf:       { label: 'Senf',           icon: '🌻', desc: 'Senf und daraus hergestellte Erzeugnisse.',                        foods: 'Senfkörner, Senfsauce, Marinaden, Dressings' },
  sesam:      { label: 'Sesam',          icon: '🌱', desc: 'Sesamsamen und daraus hergestellte Erzeugnisse.',                  foods: 'Sesamöl, Tahini, Hummus, Sushi, Burger-Buns' },
  sulfite:    { label: 'Sulfite',        icon: '⚗️', desc: 'Schwefeldioxid und Sulfite ab 10 mg/kg als Konservierungsmittel.', foods: 'Wein, Trockenfrüchte, Essig, Kartoffelprodukte' },
  lupinen:    { label: 'Lupinen',        icon: '🌿', desc: 'Lupinen und daraus hergestellte Erzeugnisse.',                     foods: 'Lupinenmehl, vegane Aufschnitte, glutenfreies Brot' },
  weichtiere: { label: 'Weichtiere',     icon: '🦑', desc: 'Weichtiere und daraus hergestellte Erzeugnisse.',                  foods: 'Muscheln, Tintenfisch, Schnecken, Austern, Calamari' },
};

function RecipeDetail() {
  const {
    recipe, loading,
    servings, setServings,
    showShareModal, setShowShareModal,
    shareConfirmed, setShareConfirmed,
    shareLink, shareCopied, shareError,
    showRevokeConfirm, setShowRevokeConfirm,
    deleteError,
    showDeleteConfirm, setShowDeleteConfirm,
    activeAllergen, setActiveAllergen,
    cookedLoading, cookedToast,
    shareModalCloseRef,
    handleOpenShareModal, handleStartDelete,
    handleCreateShareLink, handleRevokeShareLink,
    getShareUrl, handleCopyLink, handleNativeShare, handlePlatformShare,
    handleCooked, handleDelete, scaledAmount,
    user, id,
    isGuest, canEdit, tags, steps, allergens, backLink,
    addItems, removeItem, shoppingItems,
  } = useRecipeDetail();

  if (loading) {
    return (
      <div className="recipe-detail">
        <div className="loading-state">
          <div className="loader"></div>
          <p>{RD.loading}</p>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="recipe-detail">
        <div className="empty-state">
          <h3>{RD.notFound}</h3>
          <Link to="/rezepte" className="btn btn-primary">Zurück zur Übersicht</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="recipe-detail fade-in">

      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <Link to={backLink.to} className="btn btn-ghost btn-sm">{backLink.label}</Link>
        </div>
        <div className="rdh-page-actions">
          {canEdit && (
            <>
              <button onClick={handleOpenShareModal} className="btn btn-ghost btn-sm">{RD.btnShare}</button>
              <Link to={`/edit/${recipe.id}`} className="btn btn-ghost btn-sm">{RD.btnEdit}</Link>
              {showDeleteConfirm ? (
                <>
                  <span className="rdh-confirm-txt">{RD.btnDeleteConfirm}</span>
                  <button className="btn btn-danger btn-sm" onClick={handleDelete}>{RD.btnDeleteYes}</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowDeleteConfirm(false)}>{RD.btnDeleteNo}</button>
                </>
              ) : (
                <button onClick={handleStartDelete} className="btn btn-danger btn-sm">{RD.btnDelete}</button>
              )}
            </>
          )}
        </div>
      </div>

      {deleteError && <div className="rdh-error">{deleteError}</div>}

      {/* ── Share-Modal ── */}
      {showShareModal && (
        <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div
            className="share-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-modal-title"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key !== 'Tab') return;
              const focusable = e.currentTarget.querySelectorAll(
                'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
              );
              const first = focusable[0];
              const last = focusable[focusable.length - 1];
              if (e.shiftKey) {
                if (document.activeElement === first) { e.preventDefault(); last.focus(); }
              } else {
                if (document.activeElement === last) { e.preventDefault(); first.focus(); }
              }
            }}
          >
            <button className="share-modal-close" ref={shareModalCloseRef} onClick={() => setShowShareModal(false)} aria-label="Teilen-Dialog schließen"><IconClose size={16} /></button>
            <h3 id="share-modal-title">{RD.shareModalTitle}</h3>
            {shareError && <div className="rdh-error">{shareError}</div>}

            {!shareLink && !recipe.share_token ? (
              <>
                <div className="share-warning">
                  <span className="share-warning-icon"><IconWarning size={18} /></span>
                  <p>
                    <strong>Urheberrechtshinweis:</strong> {RD.shareWarningText}
                  </p>
                </div>
                <label className="share-checkbox-label">
                  <input
                    type="checkbox"
                    checked={shareConfirmed}
                    onChange={e => setShareConfirmed(e.target.checked)}
                  />
                  <span>{RD.shareCheckboxLabel}</span>
                </label>
                <button
                  className="btn btn-primary"
                  disabled={!shareConfirmed}
                  onClick={handleCreateShareLink}
                >
                  {RD.btnCreateLink}
                </button>
              </>
            ) : (
              <>
                <p className="share-modal-hint">{RD.shareLinkHint}</p>
                <div className="share-link-box">
                  <span className="share-link-text">
                    {shareLink || `${window.location.origin}/s/${recipe.share_token}`}
                  </span>
                  <button className="share-copy-btn" onClick={handleCopyLink}>
                    {shareCopied ? <IconClipboardDone size={16} /> : <IconClipboardCopy size={16} />}
                  </button>
                </div>
                {navigator.share && (
                  <button className="btn-native-share" onClick={handleNativeShare}>
                    {RD.btnNativeShare}
                  </button>
                )}
                <div className="share-platforms">
                  <button className="share-platform-btn whatsapp" onClick={() => handlePlatformShare('whatsapp')}><IconBrandWhatsapp size={20} />WhatsApp</button>
                  <button className="share-platform-btn facebook" onClick={() => handlePlatformShare('facebook')}><IconBrandFacebook size={20} />Facebook</button>
                  <button className="share-platform-btn twitter" onClick={() => handlePlatformShare('twitter')}><IconBrandX size={20} />X</button>
                  <button className="share-platform-btn telegram" onClick={() => handlePlatformShare('telegram')}><IconBrandTelegram size={20} />Telegram</button>
                  <button className="share-platform-btn linkedin" onClick={() => handlePlatformShare('linkedin')}><IconBrandLinkedin size={20} />LinkedIn</button>
                  <button className="share-platform-btn email" onClick={() => handlePlatformShare('email')}><IconMail size={20} />E-Mail</button>
                </div>
                {showRevokeConfirm ? (
                  <div className="revoke-confirm">
                    <p>{RD.revokeConfirmText}</p>
                    <div className="revoke-confirm-buttons">
                      <button className="btn btn-danger btn-sm" onClick={handleRevokeShareLink}>{RD.btnRevokeConfirm}</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setShowRevokeConfirm(false)}>{RD.btnRevokeCancel}</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn-link-revoke" onClick={() => setShowRevokeConfirm(true)}>
                    {RD.btnRevokeLink}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Flag-Hinweis ── */}
      {recipe.is_flagged && canEdit && (
        <div className="flag-notice">
          <span className="flag-notice-icon"><IconWarning size={18} /></span>
          <div>
            <strong>Dieses Rezept wird derzeit von einem Mitarbeiter überprüft.</strong>
            <p>Dein Inhalt wurde von unserem automatischen System zur manuellen Kontrolle markiert und ist vorübergehend für andere Nutzer nicht sichtbar. Wir melden uns bei dir sobald die Prüfung abgeschlossen ist. Vielen Dank für dein Verständnis.</p>
          </div>
        </div>
      )}

      {/* ── Header-Karte ── */}
      <div className="recipe-detail-header">
        <div className="rdh-img">
          {recipe.image_path
            ? <img src={recipe.image_path} alt={recipe.title} />
            : (CATEGORY_ICONS[recipe.category] || <IconPhoto size={48} />)
          }
          {recipe.image_credit && recipe.image_credit_url && (
            <a href={recipe.image_credit_url} target="_blank" rel="noopener noreferrer" className="rdh-img-credit">
              📷 {recipe.image_credit}
            </a>
          )}
        </div>
        <div className="rdh-body">
          <div className="rdh-title-row">
            <div className="rdh-title">{recipe.title}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {recipe.category && (
                <span className={`cat-badge ${catColorClass(recipe.category)}`}>{recipe.category}</span>
              )}
              {getKiLabel(recipe.content_source) && (
                <span className="ki-badge-inline" title={getKiTooltip(recipe.content_source)}>
                  <IconWand size={12} /> {getKiLabel(recipe.content_source)}
                </span>
              )}
            </div>
          </div>

          {recipe.servings > 0 && Array.isArray(recipe.ingredients) &&
            recipe.ingredients.some(ing => ing.amount && !ing.unit && (ing.amount * servings / recipe.servings) < 1) && (
            <div style={{
              fontSize: '0.78rem', color: 'var(--amber)',
              background: 'var(--amber-pale)', border: '1px solid var(--amber)',
              borderRadius: 'var(--r2)', padding: '6px 10px', marginBottom: 8,
            }}>
              <IconWarning size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Bei dieser Portionsanzahl sind manche Zutaten kleiner als 1 — bitte Mengen prüfen.
            </div>
          )}

          <div className="rdh-meta-strip">
            {recipe.servings > 0 && (
              <div className="rdh-meta-item">
                <div className="rdh-meta-label">{RD.labelPortions}</div>
                <div className="portion-ctrl" role="group" aria-label="Portionen anpassen">
                  <button className="port-btn" onClick={() => setServings(Math.max(1, servings - 1))} aria-label="Portion verringern">−</button>
                  <span className="port-num" aria-live="polite" aria-atomic="true">{servings}</span>
                  <button className="port-btn" onClick={() => setServings(servings + 1)} aria-label="Portion erhöhen">+</button>
                </div>
              </div>
            )}
            {recipe.prep_time > 0 && (
              <div className="rdh-meta-item">
                <div className="rdh-meta-label">{RD.labelPrepTime}</div>
                <div className="rdh-meta-val">{formatTime(recipe.prep_time)}</div>
              </div>
            )}
            {recipe.cook_time > 0 && (
              <div className="rdh-meta-item">
                <div className="rdh-meta-label">{RD.labelCookTime}</div>
                <div className="rdh-meta-val">{formatTime(recipe.cook_time)}</div>
              </div>
            )}
          </div>

          {tags.length > 0 && (
            <div className="rdh-tags">
              {tags.map(tag => <span key={tag} className="tag-chip">{tag}</span>)}
            </div>
          )}

          <div className="allergen-bar">
            {allergens.length === 0 ? (
              <span className="allergen-bar-none">{RD.noAllergens}</span>
            ) : (
              <>
                <div className="allergen-section-head">
                  <IconWarning size={16} className="allergen-warn-icon" />
                  <span>Allergene</span>
                </div>
                {allergens.map((key) => {
                  const a = ALLERGEN_LABELS[key];
                  return a ? (
                    <span
                      key={key}
                      className={`allergen-badge${activeAllergen === key ? ' allergen-badge--active' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-label={`${a.label} – Details anzeigen`}
                      aria-expanded={activeAllergen === key}
                      onMouseEnter={() => setActiveAllergen(key)}
                      onMouseLeave={() => setActiveAllergen(null)}
                      onClick={e => { e.stopPropagation(); setActiveAllergen(prev => prev === key ? null : key); }}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveAllergen(prev => prev === key ? null : key); } }}
                      onBlur={() => setActiveAllergen(null)}
                    >
                      {a.icon} {a.label}
                      {activeAllergen === key && (
                        <span className="allergen-tooltip">
                          <span className="allergen-tooltip-name">{a.icon} {a.label}</span>
                          <span className="allergen-tooltip-desc">{a.desc}</span>
                          <span className="allergen-tooltip-foods">z.B.: {a.foods}</span>
                        </span>
                      )}
                    </span>
                  ) : null;
                })}
                <div className="allergen-ki-hint">
                  <span className="ki-badge-inline"><IconWand size={12} /> KI-erkannt</span>
                  <span> {RD.allergenKiHint}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Zweispalten: Zutaten + Zubereitung ── */}
      <div className="recipe-cols">

        {/* Zutaten */}
        <div>
          <div className="sec-header" style={{ marginBottom: '8px' }}>
            <div className="sec-title">
              {RD.labelIngredients}
              {recipe.servings > 0 && (
                <span className="sec-title-note">{RD.labelForServings(servings)}</span>
              )}
            </div>
            {recipe.ingredients?.length > 0 && (isGuest ? (
              <a href="/login?tab=register" className="add-all-btn" title="Registrieren um Einkaufsliste zu nutzen">
                <IconNavShopping size={16} />
                Anmelden für Einkaufsliste
              </a>
            ) : (() => {
              const allInList = recipe.ingredients.every(ing =>
                shoppingItems.some(i => i.name.toLowerCase().trim() === ing.name.toLowerCase().trim() && !i.checked)
              );
              return (
                <button
                  className={`add-all-btn${allInList ? ' add-all-btn--remove' : ''}`}
                  onClick={() => {
                    if (allInList) {
                      recipe.ingredients.forEach(ing => {
                        shoppingItems
                          .filter(i => i.name.toLowerCase().trim() === ing.name.toLowerCase().trim() && !i.checked)
                          .forEach(i => removeItem(i.id));
                      });
                    } else {
                      addItems(recipe.ingredients.map(ing => ({
                        name: ing.name,
                        amount: [scaledAmount(ing), ing.unit].filter(Boolean).join(' '),
                        recipeId: recipe.id,
                      })));
                    }
                  }}
                >
                  <IconNavShopping size={16} />
                  {allInList ? RD.btnRemoveAll : RD.btnAddAll}
                </button>
              );
            })())}
          </div>
          <div className="ingredient-list">
            {recipe.ingredients?.map((ing, idx) => {
              const inList = !isGuest && shoppingItems.some(
                i => i.name.toLowerCase().trim() === ing.name.toLowerCase().trim() && !i.checked
              );
              return (
                <label key={idx} className={`ing-row${inList ? ' have' : ''}`}
                  style={isGuest ? { cursor: 'default' } : {}}>
                  <input
                    type="checkbox"
                    className="ing-checkbox"
                    checked={inList}
                    disabled={isGuest}
                    onChange={() => {
                      if (isGuest) return;
                      if (inList) {
                        shoppingItems
                          .filter(i => i.name.toLowerCase().trim() === ing.name.toLowerCase().trim() && !i.checked)
                          .forEach(i => removeItem(i.id));
                      } else {
                        addItems([{
                          name: ing.name,
                          amount: [scaledAmount(ing), ing.unit].filter(Boolean).join(' '),
                          recipeId: recipe.id,
                        }]);
                      }
                    }}
                  />
                  <span className="ing-amt">{scaledAmount(ing)} {ing.unit}</span>
                  <span className="ing-name">{ing.name}</span>
                </label>
              );
            })}
          </div>

          {/* ── Heute gekocht (Desktop: unter Zutaten) ── */}
          {user && (
            <div className="rdh-cooked-wrap">
              <button
                className={`btn btn-primary btn-sm${cookedLoading ? ' rdh-cooked-loading' : ''}`}
                onClick={handleCooked}
                disabled={cookedLoading}
                title="Zutaten werden automatisch aus dem Inventar reduziert"
              >
                Heute gekocht
              </button>
              {cookedToast && (
                <div className="rdh-cooked-toast">
                  ✓ Gekocht!
                  {cookedToast.reduced.length > 0
                    ? ` · ${cookedToast.reduced.join(' · ')} im Inventar reduziert`
                    : ' · Keine passenden Inventar-Items gefunden'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Zubereitung */}
        <div>
          <div className="sec-header" style={{ marginBottom: '12px' }}>
            <div className="sec-title">{RD.labelSteps}</div>
          </div>
          <div className="step-list">
            {steps.map((step, idx) => {
              const stepText = typeof step === 'string' ? step : (step.text || '');
              const stepImage = typeof step === 'object' && step?.image_path ? step.image_path : null;
              return (
                <div key={idx} className="step-row">
                  <div className="step-num">{idx + 1}</div>
                  <div className="step-text">
                    <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(stepText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')) }} />
                    {stepImage && (
                      <img src={stepImage} alt={`Schritt ${idx + 1}`} className="step-detail-img" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

export default RecipeDetail;
